import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { isForecastEnabled, isXeroEnabled } from '@/lib/feature-flags';
import { FinanceGuard } from '@/components/finance/FinanceGuard';
import { FinanceNav } from '@/components/tracking/FinanceNav';
import { FinanceSlider } from '@/components/finance/FinanceSlider';
import { FreshnessChip } from '@/components/tracking/FreshnessChip';
import { useFinancialModel } from '@/hooks/use-financial-model';
import { financialFreedom } from '@/lib/financial-calc/forecast';
import { netMarginPct } from '@/lib/financial-calc/ratios';
import { fmtCurrency } from '@/components/tracking/format';
import { ForecastToolNav } from '@/components/finance/ForecastToolNav';

/**
 * /finance/forecast/financial-freedom — Financial Freedom (F-013 S6.9). Works BACKWARD from the owner's
 * target take-home to the revenue the business must do. Net margin is seeded from the REAL model; the gap
 * is measured against the real revenue run-rate. Net margin ≤ 0 → honest "can't fund a wage from a loss".
 */
export const Route = createFileRoute('/finance/forecast_/financial-freedom')({
  beforeLoad: () => {
    if (!isXeroEnabled() || !isForecastEnabled()) {
      throw redirect({ to: '/finance', replace: true });
    }
  },
  component: () => (
    <FinanceGuard>
      <FinancialFreedom />
    </FinanceGuard>
  ),
});


function FinancialFreedom() {
  const { model, input } = useFinancialModel();
  const periods = input?.periods ?? [];
  const latest = periods[periods.length - 1];
  const rt = (latest?.roleTotals ?? {}) as Record<string, number>;

  // Seed net margin + current revenue run-rate from the REAL model (latest month × 12).
  const seeded = useMemo(() => {
    const margin = netMarginPct(rt);
    const monthlyRevenue = rt.revenue ?? 0;
    return { margin, annualRevenue: monthlyRevenue * 12 };
  }, [rt]);

  const [takeHome, setTakeHome] = useState(150_000);
  const [taxBuffer, setTaxBuffer] = useState(30);
  // Net margin is editable but seeded from real data — the builder can model a target margin.
  const [margin, setMargin] = useState<number | null>(null);
  const marginPct = margin ?? seeded.margin ?? 0;

  const f = financialFreedom(takeHome, taxBuffer, marginPct, seeded.annualRevenue);

  if (!model || periods.length === 0) {
    return (
      <div className="mx-auto w-full max-w-6xl">
        <FinanceNav />
        <div className="mt-8 rounded-2xl border border-dashed border-border py-16 text-center text-muted-foreground">
          <p className="font-semibold text-foreground">Needs your Xero data</p>
          <p className="mt-1 text-[12.5px]">Connect Xero and sync so we can read your margin and revenue run-rate.</p>
        </div>
      </div>
    );
  }

  const Stat = ({ l, v, tone, note }: { l: string; v: string; tone?: 'good' | 'bad'; note?: string }) => (
    <div className="rounded-2xl border border-border bg-card p-4">
      <p className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-muted-foreground">{l}</p>
      <p className={`mt-1 ds-kpi-md ${tone === 'good' ? 'text-success' : tone === 'bad' ? 'text-danger' : 'text-foreground'}`}>{v}</p>
      {note && <p className="mt-1 text-[11.5px] text-muted-foreground">{note}</p>}
    </div>
  );

  const cannotCompute = f.requiredRevenue === null;

  return (
    <div className="mx-auto w-full max-w-6xl">
      <FinanceNav />
      <Link to="/finance/forecast" className="mt-2 inline-flex items-center gap-1 text-[12px] text-muted-foreground no-underline hover:text-foreground">
        <ChevronLeft className="h-3.5 w-3.5" /> Forecast
      </Link>
      <div className="mt-1 flex flex-wrap items-center justify-between gap-2"><h1 className="ds-h1">Financial Freedom</h1><FreshnessChip fetchedAt={model?.fetchedAt} /></div>
      <p className="mb-4 mt-0.5 text-[13px] text-muted-foreground">Start from the life you want, work back to the revenue that funds it. ({model.period})</p>

      <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-4">
          <p className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.06em] text-muted-foreground">What you want</p>
          <div className="flex flex-col gap-2.5">
            <FinanceSlider label="Target take-home / yr" value={takeHome} set={setTakeHome} min={50_000} max={500_000} step={5_000} fmt={fmtCurrency} />
            <FinanceSlider label="Tax buffer" value={taxBuffer} set={setTaxBuffer} min={0} max={50} step={1} fmt={(v) => `${v}%`} />
            <FinanceSlider label="Net margin" value={Number(marginPct.toFixed(1))} set={setMargin} min={1} max={60} step={0.5} fmt={(v) => `${v}%`} />
          </div>
          <p className="mt-3 text-[11px] text-muted-foreground">
            {seeded.margin == null ? 'No computable margin in your figures yet — set the margin you want to model.' : `Margin is seeded from your real figures (${seeded.margin.toFixed(1)}%). Adjust it to model a target — reset by re-entering the seeded value.`}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:col-span-2">
          <Stat l="Net profit needed" v={fmtCurrency(f.requiredNetProfit)} note={`${fmtCurrency(takeHome)} take-home + ${taxBuffer}% tax buffer`} />
          {cannotCompute ? (
            <div className="rounded-2xl border border-dashed border-border p-4 sm:col-span-2">
              <p className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-muted-foreground">Revenue needed</p>
              <p className="mt-1 ds-kpi-md text-foreground">—</p>
              <p className="mt-1 text-[11.5px] text-muted-foreground">{seeded.margin == null && margin == null ? 'No computable net margin in your figures yet' : `Your net margin is ${marginPct.toFixed(1)}%`} — at or below zero you can't scale revenue into a wage. Lift the margin first (pricing, overhead), then set a target here.</p>
            </div>
          ) : (
            <>
              <Stat l="Revenue needed / yr" v={fmtCurrency(f.requiredRevenue!)} note={`at a ${marginPct.toFixed(1)}% net margin`} />
              <Stat l="Monthly revenue target" v={fmtCurrency(f.monthlyRevenueTarget!)} note="the run-rate that funds it" />
              <Stat
                l="Gap vs today"
                v={`${f.revenueGap! > 0 ? '+' : ''}${fmtCurrency(f.revenueGap!)}`}
                tone={f.revenueGap! > 0 ? 'bad' : 'good'}
                note={
                  f.gapPct === null
                    ? `current run-rate ${fmtCurrency(seeded.annualRevenue)}/yr`
                    : f.revenueGap! > 0
                      ? `${f.gapPct.toFixed(0)}% more than today's ${fmtCurrency(seeded.annualRevenue)}/yr`
                      : `you're already there — ${fmtCurrency(seeded.annualRevenue)}/yr covers it`
                }
              />
            </>
          )}
        </div>
      </div>
      {/* The gap, visually: today's run-rate vs the revenue the lifestyle needs. */}
      {!cannotCompute && f.requiredRevenue != null && (
        <div className="mt-3.5 rounded-2xl border border-border bg-card p-4">
          <p className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.06em] text-muted-foreground">Today vs the target</p>
          {(() => {
            const maxV = Math.max(seeded.annualRevenue, f.requiredRevenue!) || 1;
            const curPct = (seeded.annualRevenue / maxV) * 100;
            const needPct = (f.requiredRevenue! / maxV) * 100;
            const gapAhead = f.revenueGap != null && f.revenueGap <= 0;
            return (
              <div className="flex flex-col gap-2">
                <div>
                  <div className="mb-0.5 flex justify-between text-[11px] text-muted-foreground"><span>Current run-rate</span><span className="font-mono">{fmtCurrency(seeded.annualRevenue)}/yr</span></div>
                  <div className="h-4 overflow-hidden rounded bg-muted"><div className="h-4 rounded bg-[hsl(var(--primary))]" style={{ width: `${curPct}%` }} /></div>
                </div>
                <div>
                  <div className="mb-0.5 flex justify-between text-[11px] text-muted-foreground"><span>Needed for the lifestyle</span><span className="font-mono">{fmtCurrency(f.requiredRevenue!)}/yr</span></div>
                  <div className="relative h-4 overflow-hidden rounded bg-muted">
                    <div className={`h-4 rounded ${gapAhead ? 'bg-success/60' : 'bg-success/50'}`} style={{ width: `${Math.min(curPct, needPct)}%` }} />
                    {!gapAhead && (
                      <div className="absolute top-0 h-4 bg-warning/60" style={{ left: `${curPct}%`, width: `${needPct - curPct}%` }} title="The gap" />
                    )}
                  </div>
                </div>
                <p className="text-[11.5px] text-muted-foreground">{gapAhead
                  ? 'You’re already past the target — the shaded bar is fully covered.'
                  : 'The amber stretch is the gap — the extra revenue the lifestyle needs at this margin.'}</p>
              </div>
            );
          })()}
        </div>
      )}

      <p className="mt-4 text-[11px] text-muted-foreground">Net margin and current revenue come from your latest synced month (annualised). The tax buffer is a planning allowance, not a tax calculation — confirm the real number with your accountant.</p>
      <ForecastToolNav current="financial-freedom" />
    </div>
  );
}
